import { api, mediaUrl } from './api';

export type UploadResult = {
  id: string;
  kind: 'image' | 'audio' | 'pdf';
  contentType: string;
  size: number;
  originalName: string;
  url: string;
};

// Upload a browser File to the media endpoint. For images we first strip
// metadata (EXIF/GPS/camera/timestamps) by re-encoding through a canvas —
// keeping photos from leaking location or device fingerprints. Audio and PDFs
// pass through untouched; their metadata is typically benign and stripping
// would require format-specific parsers.
export async function uploadMedia(file: File): Promise<UploadResult & { absoluteUrl: string }> {
  const clean = file.type.startsWith('image/') ? await stripImageMetadata(file) : file;
  const form = new FormData();
  form.append('file', clean);
  const res = await api.upload<UploadResult>('/media', form);
  return { ...res, absoluteUrl: mediaUrl(res.id) };
}

// Decode → draw to canvas → re-export. The canvas API emits clean pixels with
// no EXIF, XMP, or IPTC blocks. JPEGs stay JPEGs (slight requant), PNG stays
// PNG (lossless but may change file size). Uncommon or browser-unsupported
// formats (HEIC, AVIF) fall through unprocessed — better to let the upload
// proceed than fail silently on a format we can't decode.
async function stripImageMetadata(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    // PNG re-encodes losslessly. Everything else goes through JPEG at quality
    // 0.92 — close to the source quality for photos without bloating PNGs of
    // screenshots by converting them from lossless.
    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const quality = outputType === 'image/jpeg' ? 0.92 : undefined;
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, outputType, quality),
    );
    if (!blob) return file;

    // Keep the user's filename but switch the extension if we changed format.
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    const ext = outputType === 'image/png' ? 'png' : 'jpg';
    return new File([blob], `${baseName}.${ext}`, {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}
